import { useState, useCallback } from "react";
import {
  Maximize2,
  CornerUpLeft,
  CornerUpRight,
  CornerDownLeft,
  CornerDownRight,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ColorPicker } from "@/components/ui/color-picker";
import { TailwindColorSelectorPopover } from "@/components/editor/themes/components/tailwind-color-selector-popover";
import { SizeInput } from "@/components/ui/size-input";
import { cn } from "@/lib/utils";

import PropertySection from "../PropertySection";
import { BORDER_STYLES } from "@/constants/property-panel.constants";

interface BorderSectionProps {
  styles: Record<string, string>;
  onStyleChange: (property: string, value: string) => void;
  onBatchStyleChange: (updates: Record<string, string>) => void;
}

/** Compact radius input with corner icon */
function RadiusInput({
  icon: Icon,
  value,
  onChange,
}: {
  icon: LucideIcon;
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === "") {
        onChange("");
        return;
      }
      // Allow digits and common units
      onChange(raw);
    },
    [onChange],
  );

  return (
    <div className="flex items-center gap-1.5 rounded-md border border-input bg-background px-2 h-8 min-w-0">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <input
        type="text"
        value={value || ""}
        onChange={handleChange}
        placeholder="0px"
        className="w-full bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
      />
    </div>
  );
}

export const BorderSection = ({
  styles,
  onStyleChange,
  onBatchStyleChange,
}: BorderSectionProps) => {
  const [radiusExpanded, setRadiusExpanded] = useState(false);

  const handleUniformRadiusChange = useCallback(
    (value: string) => {
      onBatchStyleChange({
        borderRadius: value,
        borderTopLeftRadius: value,
        borderTopRightRadius: value,
        borderBottomLeftRadius: value,
        borderBottomRightRadius: value,
      });
    },
    [onBatchStyleChange],
  );

  return (
    <PropertySection title="Border">
      <div className="flex flex-col gap-3">
        {/* Color */}
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-muted-foreground">Color</Label>
          <div className="flex items-center gap-2">
            <ColorPicker
              value={styles.borderColor}
              onChange={(value) => onStyleChange("borderColor", value)}
              className="flex-shrink-0 h-8 w-8 border-border"
            />
            <TailwindColorSelectorPopover
              value={styles.borderColor}
              onSelect={(color) => onStyleChange("borderColor", color)}
            />
            <Input
              value={styles.borderColor}
              onChange={(e) => onStyleChange("borderColor", e.target.value)}
              className="h-8 bg-background border-border text-foreground text-xs font-mono"
              placeholder="#000000"
            />
          </div>
        </div>

        {/* Width + Style side-by-side */}
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">Width</Label>
            <SizeInput
              value={styles.borderWidth}
              onChange={(value) => onStyleChange("borderWidth", value)}
              placeholder="0"
              units={["px", "rem", "em"]}
              defaultUnit="px"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">Style</Label>
            <Select
              onValueChange={(value) => onStyleChange("borderStyle", value)}
              value={styles.borderStyle}
            >
              <SelectTrigger className="w-full bg-background border border-border rounded-md text-foreground h-8 text-xs">
                <SelectValue placeholder="Style" />
              </SelectTrigger>
              <SelectContent>
                {BORDER_STYLES.map((style) => (
                  <SelectItem key={style} className="text-xs" value={style}>
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Border Radius — linked/expanded like Spacing */}
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-muted-foreground">
            Border Radius
          </Label>
          {!radiusExpanded ? (
            /* Uniform mode: single input + toggle */
            <div className="flex items-center gap-1.5">
              <div className="flex-1 min-w-0">
                <SizeInput
                  value={styles.borderRadius}
                  onChange={handleUniformRadiusChange}
                  placeholder="0"
                  units={["px", "%", "rem", "em"]}
                  defaultUnit="px"
                />
              </div>
              <button
                type="button"
                onClick={() => setRadiusExpanded(true)}
                className="shrink-0 h-8 w-8 flex items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer"
                title="Edit individual corners"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            /* Expanded mode: 4 corner inputs + toggle */
            <div className="flex items-start gap-1.5">
              <div className="flex-1 min-w-0 grid grid-cols-2 gap-1.5">
                <RadiusInput
                  icon={CornerUpLeft}
                  value={styles.borderTopLeftRadius}
                  onChange={(v) => onStyleChange("borderTopLeftRadius", v)}
                />
                <RadiusInput
                  icon={CornerUpRight}
                  value={styles.borderTopRightRadius}
                  onChange={(v) => onStyleChange("borderTopRightRadius", v)}
                />
                <RadiusInput
                  icon={CornerDownLeft}
                  value={styles.borderBottomLeftRadius}
                  onChange={(v) => onStyleChange("borderBottomLeftRadius", v)}
                />
                <RadiusInput
                  icon={CornerDownRight}
                  value={styles.borderBottomRightRadius}
                  onChange={(v) => onStyleChange("borderBottomRightRadius", v)}
                />
              </div>
              <button
                type="button"
                onClick={() => setRadiusExpanded(false)}
                className={cn(
                  "shrink-0 h-8 w-8 flex items-center justify-center rounded-md border transition-colors cursor-pointer",
                  "border-primary bg-primary text-primary-foreground hover:bg-primary/90",
                )}
                title="Link corners"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </PropertySection>
  );
};
